import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext, StateToken } from '@ngxs/store';
import { patch } from '@ngxs/store/operators';
import { tap } from 'rxjs';
import { AdminOrderRoutes } from 'src/app/core/enums/routes.enum';
import { ApiResponse } from 'src/app/core/models/api-response';
import { OnSuccess } from 'src/app/shared/decorators/on-success.decorator';
import { ResetCurrentFilter } from 'src/app/core/state/main-table-filter.actions';
import { IOrderTypeResponse } from '../../models/order-type.model';
import { OrderTypeApiService } from '../../services/order-type-api.service';
import {
  CreateOrderType,
  DeleteOrderType,
  LoadAllOrderTypes,
  LoadOrderTypeById,
  LoadOrderTypes,
  UpdateOrderType,
  ClearCurrentOrderType,
} from './order-type.actions';

export interface OrderTypeStateModel {
  orderTypesLoaded: boolean;
  orderTypes: IOrderTypeResponse[];
  allOrderTypes: IOrderTypeResponse[];
  orderTypesTotalPages: number;
  currentOrderType: IOrderTypeResponse;
}

const ORDER_TYPE_STATE_TOKEN = new StateToken<OrderTypeStateModel>('orderTypes');

@State<OrderTypeStateModel>({
  name: ORDER_TYPE_STATE_TOKEN,
  defaults: {
    orderTypesLoaded: false,
    orderTypes: [],
    allOrderTypes: [],
    orderTypesTotalPages: 0,
    currentOrderType: <IOrderTypeResponse>{},
  },
})
@Injectable()
export class OrderTypeState {
  constructor(private service: OrderTypeApiService) {}

  @Action(LoadOrderTypes, { cancelUncompleted: true })
  loadOrderTypes(
    { getState, setState }: StateContext<OrderTypeStateModel>,
    { filter }: LoadOrderTypes
  ) {
    return this.service.getPagedOrderTypes(filter).pipe(
      tap((response: ApiResponse<IOrderTypeResponse[]>) => {
        const state = getState();
        setState({
          ...state,
          orderTypesLoaded: true,
          orderTypes: response.data,
          orderTypesTotalPages: response.totalPages,
        });
      })
    );
  }

  @Action(LoadAllOrderTypes, { cancelUncompleted: true })
  loadAllOrderTypes(ctx: StateContext<OrderTypeStateModel>, { departmentId }: LoadAllOrderTypes) {
    return this.service.getAllOrderTypes(departmentId).pipe(
      tap((allOrderTypes: IOrderTypeResponse[]) => {
        ctx.setState(patch({ allOrderTypes }));
      })
    );
  }

  @Action(LoadOrderTypeById, { cancelUncompleted: true })
  loadOrderTypeById(
    { getState, setState }: StateContext<OrderTypeStateModel>,
    { id }: LoadOrderTypeById
  ) {
    return this.service.getOrderTypeById(id).pipe(
      tap((response: IOrderTypeResponse) => {
        const state = getState();
        setState({
          ...state,
          orderTypesLoaded: true,
          currentOrderType: response,
        });
      })
    );
  }

  @Action(CreateOrderType, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.OrderTypes, message: 'orderTypeCreated' })
  createOrderType(ctx: StateContext<OrderTypeStateModel>, { orderType }: CreateOrderType) {
    return this.service.createOrderType(orderType);
  }

  @Action(UpdateOrderType, { cancelUncompleted: true })
  @OnSuccess({ url: AdminOrderRoutes.OrderTypes, message: 'orderTypeUpdated' })
  updateOrderType(ctx: StateContext<OrderTypeStateModel>, { orderType }: UpdateOrderType) {
    return this.service.updateOrderType(orderType);
  }

  @Action(DeleteOrderType, { cancelUncompleted: true })
  @OnSuccess({
    url: AdminOrderRoutes.OrderTypes,
    message: 'orderTypeDeleted',
    dispatch: ResetCurrentFilter,
  })
  deleteOrderType(ctx: StateContext<OrderTypeStateModel>, { orderTypeId }: DeleteOrderType) {
    return this.service.deleteOrderType(orderTypeId);
  }

  @Action(ClearCurrentOrderType)
  clearCurrentOrderType(ctx: StateContext<OrderTypeStateModel>) {
    ctx.setState(patch({ currentOrderType: <IOrderTypeResponse>{} }));
  }

  @Selector()
  static orderTypesLoaded(state: OrderTypeStateModel) {
    return state.orderTypesLoaded;
  }

  @Selector()
  static getOrderTypesTotalPages(state: OrderTypeStateModel) {
    return state.orderTypesTotalPages;
  }

  @Selector()
  static getPagedOrderTypes(state: OrderTypeStateModel) {
    return state.orderTypes;
  }

  @Selector()
  static getAllOrderTypes(state: OrderTypeStateModel) {
    return state.allOrderTypes;
  }

  @Selector()
  static getOrderTypeById(state: any) {
    return state.currentOrderType;
  }
}
